import { getGraphClient, parseSiteUrl } from './sharePointService';
import { decodeBase64ToUint8Array } from './VectorUtils';
import { getCopilotDexieDB } from './CopilotDexieDB';

export type EmbeddingsMode = 'meta' | 'deep';

export interface IEmbeddingRecord {
    ID: number;
    Title: string;
    Path?: string;
    Keywords?: string[];
    ModelName?: string;
    ContentHash?: string;
    Modified?: string;
    Created?: string;
    Intent?: string;
    IntentKey?: string;
    Category?: string;
    CategoryKey?: string;
    CategoryConfidence?: number;
    DeepVector?: string;
    DeepSignatureBytes?: Uint8Array; // Local-only: Decoded from DeepVector for fast math
    Readiness?: number; // 0-100 overall score
    Readability?: number;
    StructureScore?: number;
    AEW?: number;
    MatrixQuadrant?: string;
    AnalysisReport?: object; // The detailed report JSON object generated by the worker
    SourcePageId?: number;  // Site Pages list item ID — stable even if page is renamed
}

export interface IEmbeddingsInitOptions {
    listName: string;
    modelName: string;
    mode: EmbeddingsMode;
    maxTotalIndexItems?: number;
    initialLoadPercentage?: number;
    cachingStrategy: 'Off' | 'Session' | 'Local';
}

function normalizeIntentKey(record: any): string {
    let intent = record.Intent || record.IntentType || 'default';
    if (typeof intent === 'string') {
        return intent.trim().toLowerCase();
    }
    return 'default';
}

function getDedupeKey(record: any): string {
    const logicalKey = record.Title;
    const intentKey = normalizeIntentKey(record);
    return `${logicalKey}::${intentKey}`;
}

function dedupeEmbeddingRecords(records: any[]): any[] {
    const groups = new Map<string, any[]>();
    for (const rec of records) {
        const key = getDedupeKey(rec);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(rec);
    }

    const merged: any[] = [];
    for (const group of groups.values()) {
        if (group.length === 1) {
            merged.push(group[0]);
            continue;
        }
        let winner = group[0];
        merged.push(winner); // simplified dedupe logic for brevity, takes first or could be fleshed out
    }
    return merged;
}

export class EmbeddingsIndexService {
    private _index: IEmbeddingRecord[] = [];
    private _cacheKey = '';
    private readonly _cacheVersion = 'v4-copilot-embeddings'; // bumped: added AEW and MatrixQuadrant

    public async initialize(siteUrl: string, options: IEmbeddingsInitOptions): Promise<void> {
        const {
            listName,
            modelName,
            mode,
            maxTotalIndexItems = 5000,
            initialLoadPercentage = 100,
            cachingStrategy = 'Local'
        } = options;

        const siteKey = siteUrl.replace(/https?:\/\//, '').replace(/\//g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
        this._cacheKey = `CopilotEmbeddingsIndex:v5:${siteKey}:${listName}:${mode}:${modelName}`;


        const cached = await this._getCacheAsync(cachingStrategy);
        // Guard: discard cache if any record is missing AnalysisReport OR SourcePageId —
        // forces a fresh SP fetch so legacy pages get tracked correctly against stable item IDs.
        const cacheIsComplete = cached && cached.length > 0 && cached.every(r =>
            r.AnalysisReport && Object.keys(r.AnalysisReport).length > 0 && typeof r.SourcePageId === 'number'
        );
        if (cacheIsComplete) {
            console.log(`[EmbeddingsIndexService] Loaded ${cached!.length} items from cache`);
            this._index = cached!;
            return;
        }

        console.log(`[EmbeddingsIndexService] Fetching from list "${listName}"`);
        try {
            const client = await getGraphClient();
            const { hostname, path } = parseSiteUrl(siteUrl);

            const siteIds = await client.api(`/sites/${hostname}:${path}`).select('id').get();
            if (!siteIds || !siteIds.id) throw new Error('Could not resolve site ID');
            const siteId = siteIds.id;

            const lists = await client.api(`/sites/${siteId}/lists`)
                .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
                .filter(`displayName eq '${listName}'`)
                .select('id')
                .get();

            if (!lists.value || lists.value.length === 0) {
                console.warn(`[EmbeddingsIndexService] List ${listName} does not exist`);
                this._index = [];
                return;
            }
            const listId = lists.value[0].id;

            const itemsResponse = await client.api(`/sites/${siteId}/lists/${listId}/items`)
                .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
                .expand('fields($select=Id,Title,Keywords,ModelName,DeepVector,ContentHash,Intent,IntentKey,Modified,Created,Category,Readiness,Readability,StructureScore,AEW,MatrixQuadrant,AnalysisReport,SourcePageId)')
                .top(maxTotalIndexItems)
                .get();

            const items = itemsResponse.value.map((i: any) => i.fields);

            console.log(`[EmbeddingsIndexService] Retrieved ${items.length} items`);

            const records: IEmbeddingRecord[] = items.map((it: any) => {
                let parsedReport = {};
                if (it.AnalysisReport) {
                    try { parsedReport = JSON.parse(it.AnalysisReport); } catch (e) { }
                }

                const rec: IEmbeddingRecord = {
                    ID: it.Id || 0,
                    Title: it.Title || 'Untitled',
                    Keywords: it.Keywords ? String(it.Keywords).split(';').filter(Boolean) : [],
                    ModelName: it.ModelName,
                    ContentHash: it.ContentHash,
                    Intent: it.Intent,
                    Modified: it.Modified,
                    Created: it.Created,
                    Readiness: Number(it.Readiness) || 0,
                    Readability: Number(it.Readability) || 0,
                    StructureScore: Number(it.StructureScore) || 0,
                    AEW: Number(it.AEW) || 0,
                    MatrixQuadrant: it.MatrixQuadrant || 'Unknown',
                    AnalysisReport: parsedReport,
                    SourcePageId: it.SourcePageId ? Number(it.SourcePageId) : undefined
                };

                if (it.DeepVector) {
                    rec.DeepVector = String(it.DeepVector).trim();
                    rec.DeepSignatureBytes = decodeBase64ToUint8Array(rec.DeepVector);
                }
                return rec;
            });

            const dedupedRecords = dedupeEmbeddingRecords(records);
            const take = Math.max(1, Math.min(100, initialLoadPercentage));
            const count = Math.floor((dedupedRecords.length * take) / 100);

            this._index = dedupedRecords.slice(0, count);

            await this._setCacheAsync(cachingStrategy, this._index);
        } catch (e) {
            console.error("[EmbeddingsIndexService] Initialization failed. List might not exist.", e);
            this._index = [];
        }
    }

    public getItems(): IEmbeddingRecord[] {
        return this._index;
    }

    public async upsertRecord(siteUrl: string, listName: string, record: IEmbeddingRecord): Promise<void> {
        // ── 1. Update in-memory index immediately ────────────────────────────
        // Prefer matching by SourcePageId (stable SP integer), fall back to ID or Title.
        const memIdx = this._index.findIndex(ex =>
            (record.SourcePageId && ex.SourcePageId === record.SourcePageId) ||
            (record.ID > 0 && ex.ID === record.ID) ||
            ex.Title === record.Title
        );
        if (memIdx !== -1) {
            this._index[memIdx] = record;
        } else {
            this._index.push(record);
        }

        // ── 2. Write to SharePoint ────────────────────────────────────────────
        try {
            const client = await getGraphClient();
            const { hostname, path } = parseSiteUrl(siteUrl);

            const siteIds = await client.api(`/sites/${hostname}:${path}`).select('id').get();
            if (!siteIds || !siteIds.id) throw new Error('Could not resolve site ID');
            const siteId = siteIds.id;

            const lists = await client.api(`/sites/${siteId}/lists`)
                .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
                .filter(`displayName eq '${listName}'`)
                .select('id')
                .get();

            if (!lists.value || lists.value.length === 0) {
                console.warn(`[EmbeddingsIndexService] List ${listName} does not exist`);
                return;
            }
            const listId = lists.value[0].id;

            const payload = {
                fields: {
                    Title: record.Title || 'Untitled',
                    ContentHash: record.ContentHash || '',
                    ModelName: record.ModelName || 'Snowflake/snowflake-arctic-embed-xs',
                    Intent: record.Intent || 'None',
                    IntentKey: record.Intent || 'None',
                    Category: record.Intent || 'General',
                    CategoryKey: record.Intent || 'General',
                    CategoryConfidence: '100', // Hardcode to 100 for legacy compatibility
                    DeepVector: record.DeepVector || '',
                    Readiness: String(record.Readiness ?? 0),
                    Readability: String(record.Readability ?? 0),
                    StructureScore: String(record.StructureScore ?? 0),
                    AEW: String(record.AEW ?? 0),
                    MatrixQuadrant: record.MatrixQuadrant || 'Unknown',
                    Keywords: record.Keywords?.join(', ') || '',
                    AnalysisReport: record.AnalysisReport ? JSON.stringify(record.AnalysisReport) : '',
                    ...(record.SourcePageId ? { SourcePageId: String(record.SourcePageId) } : {})

                }
            };

            if (record.ID > 0) {
                try {
                    // Try update existing item
                    await client.api(`/sites/${siteId}/lists/${listId}/items/${record.ID}/fields`)
                        .header('If-Match', '*')
                        .patch(payload.fields);
                } catch (patchErr: any) {
                    const is404 = patchErr?.statusCode === 404 || String(patchErr?.message).includes('not found');
                    if (is404) {
                        // Stale ID — item was deleted from SP. Create a new record instead.
                        console.warn(`[EmbeddingsIndexService] PATCH 404 for ID ${record.ID} ("${record.Title}") — creating new item`);
                        const res = await client.api(`/sites/${siteId}/lists/${listId}/items`).post(payload);
                        if (res?.id) {
                            record.ID = res.id;
                            // Sync corrected ID back into memory
                            const idx = this._index.findIndex(ex => ex.Title === record.Title);
                            if (idx !== -1) this._index[idx].ID = res.id;
                        }
                    } else {
                        throw patchErr; // non-404 errors bubble up
                    }
                }
            } else {
                // record.ID = 0 → this page has no existing embedding record.
                // The caller (LocalIndexService) determined this via the in-memory
                // existingByPageId map (keyed by SourcePageId). Just POST a new record.
                const res = await client
                    .api(`/sites/${siteId}/lists/${listId}/items`)
                    .post(payload);
                if (res?.id) {
                    record.ID = Number(res.id);
                    // Sync new SP ID back into the in-memory index so future upserts
                    // in the same session can PATCH instead of POST again.
                    const memIdx = this._index.findIndex(ex =>
                        (record.SourcePageId && ex.SourcePageId === record.SourcePageId) ||
                        ex.Title === record.Title
                    );
                    if (memIdx !== -1) {
                        this._index[memIdx].ID = Number(res.id);
                    } else {
                        // It's a genuinely new mapping not in our in-memory index yet.
                        // We MUST push it to this._index so that saveCache() stores it!
                        this._index.push(record);
                    }
                }
            }

        } catch (e) {
            console.error('[EmbeddingsIndexService] Upsert failed — data is in memory but NOT saved to SharePoint', e);
        }
    }

    public async saveCache(strategy: IEmbeddingsInitOptions['cachingStrategy'] = 'Local'): Promise<void> {
        // Only cache records that were successfully written to SP (ID > 0).
        // Records with ID=0 had a failed POST — caching them means the next scan
        // sees cacheIsComplete=true, skips the SP re-fetch, and ends up POSTing
        // a new record (duplicate) instead of PATCHing the existing one.
        const validRecords = this._index.filter(r => r.ID > 0);
        await this._setCacheAsync(strategy, validRecords);
    }


    /**
     * Delete ALL records from the embeddings SP list, clear in-memory index and IndexedDB cache.
     * Used by the "Reset All Reports" admin action.
     */
    public async deleteAllRecords(siteUrl: string, listName: string): Promise<void> {
        console.log('[EmbeddingsIndexService] deleteAllRecords started...');
        try {
            const client = await getGraphClient();
            const { hostname, path } = parseSiteUrl(siteUrl);

            const siteResp = await client.api(`/sites/${hostname}:${path}`).select('id').get();
            const siteId = siteResp.id;

            const lists = await client
                .api(`/sites/${siteId}/lists`)
                .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
                .filter(`displayName eq '${listName}'`)
                .select('id')
                .get();

            if (!lists.value || lists.value.length === 0) {
                console.warn('[EmbeddingsIndexService] deleteAllRecords: list not found');
                this._index = [];
                return;
            }
            const listId = lists.value[0].id;

            // Fetch all item IDs in batches
            let nextLink: string | undefined = undefined;
            const allIds: number[] = [];
            do {
                const url = nextLink ||
                    `/sites/${siteId}/lists/${listId}/items?$select=id&$top=500`;
                const batch: any = await client.api(url)
                    .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
                    .get();
                for (const item of batch.value) allIds.push(item.id);
                nextLink = batch['@odata.nextLink'];
            } while (nextLink);

            console.log(`[EmbeddingsIndexService] Deleting ${allIds.length} items...`);

            // Delete sequentially (Graph API doesn't support batch delete)
            for (const id of allIds) {
                await client
                    .api(`/sites/${siteId}/lists/${listId}/items/${id}`)
                    .delete();
            }

            console.log('[EmbeddingsIndexService] All records deleted from SP.');
        } catch (e) {
            console.error('[EmbeddingsIndexService] deleteAllRecords failed', e);
            throw e;
        } finally {
            // Always clear memory + cache even if SP delete partially failed
            this._index = [];
            try {
                const db = getCopilotDexieDB();
                await db.put(this._cacheKey, [], this._cacheVersion);
            } catch { /* best-effort */ }
        }
    }

    private async _getCacheAsync(strategy: IEmbeddingsInitOptions['cachingStrategy']): Promise<IEmbeddingRecord[] | null> {
        try {
            if (strategy !== 'Local') return null;
            const db = getCopilotDexieDB();
            const raw = await db.get(this._cacheKey, this._cacheVersion);
            if (!raw) return null;

            const list: IEmbeddingRecord[] = (raw as any[]).map((r: any) => {
                const deepBytes = r.DeepVector ? decodeBase64ToUint8Array(r.DeepVector) : undefined;
                return {
                    ...r,
                    DeepSignatureBytes: deepBytes && deepBytes.length > 0 ? deepBytes : undefined
                };
            });
            return list;
        } catch (err) {
            console.warn('[EmbeddingsIndexService] Cache read failed', err);
            return null;
        }
    }

    private async _setCacheAsync(strategy: IEmbeddingsInitOptions['cachingStrategy'], data: IEmbeddingRecord[]): Promise<void> {
        try {
            if (strategy !== 'Local') return;
            const db = getCopilotDexieDB();

            const serializedObj = data.map(d => ({
                ...d,
                DeepSignatureBytes: undefined,
                MetaSignatureBytes: undefined
            }));

            await db.put(this._cacheKey, serializedObj, this._cacheVersion);
        } catch (err) {
            console.warn('[EmbeddingsIndexService] Failed to persist cache', err);
        }
    }
}
